/**
 * D-EZ-107 / D-EZ-108 — ONE DOOR INTO THE PEOPLE LAYER.
 *
 * Build it once per screen (it folds the league's history), then ask it about
 * anyone: `personalityOf(id, ctx)`, `quote(fact, id, ctx)`, `ctx.book` for the
 * rivalry board. Pure; reads the archived seasons and the live one, cut at
 * `throughRace` so nothing after the moment being shown is known.
 */

import { buildRegistry, type DriverRegistry } from '../history/registry.ts';
import { allRaces, type HistorySeason } from '../history/source.ts';
import { driverStreaks, type DriverStreaks } from '../history/milestones.ts';
import { ageOf } from '../aging.ts';
import { derivePersonality, type Personality } from './personality.ts';
import { rivalrySeasons, rivalryState, type RivalryBook, type RivalryInput } from './rivalry.ts';

export interface PeopleContext {
  /** Distinguishes this league's "drv-012" from another league's. */
  leagueKey: string;
  /** The seasons read, cut at `asOf`. */
  seasons: HistorySeason[];
  registry: DriverRegistry;
  streaks: Record<string, DriverStreaks>;
  book: RivalryBook;
  asOf: { seasonNumber: number; raceNumber: number } | null;
  /** Memo for `personalityOf`. */
  cache: Map<string, Personality>;
}

/**
 * The league's key: the id of its first season (`season-<seed>`), which every
 * season of a league inherits and no two leagues share by accident. An
 * exhibition has no league; pass a key of your own (the race id).
 */
export function leagueKeyOf(seasons: readonly HistorySeason[]): string {
  const first = [...seasons].sort((a, b) => a.seasonNumber - b.seasonNumber)[0];
  return first ? `${first.season.seasonId}#${first.seasonNumber}` : 'league';
}

/**
 * The seasons as they stood after `through`: later seasons dropped, later races
 * of the cut season dropped, and the cut season's table set to the one after
 * the last race kept (no champion yet, no winter).
 */
export function cutSeasons(seasons: readonly HistorySeason[], through: { seasonNumber: number; raceNumber: number } | null | undefined): HistorySeason[] {
  if (!through) return [...seasons];
  const out: HistorySeason[] = [];
  for (const s of seasons) {
    if (s.seasonNumber > through.seasonNumber) continue;
    if (s.seasonNumber < through.seasonNumber) {
      out.push(s);
      continue;
    }
    const races = s.season.races.filter((r) => r.raceNumber <= through.raceNumber);
    if (races.length === s.season.races.length && s.complete !== false && races.length > 0 && through.raceNumber >= Math.max(...races.map((r) => r.raceNumber))) {
      // Every race of a settled season: still the season, but the winter is after the moment.
      out.push({ ...s, offseason: undefined });
      continue;
    }
    const last = races[races.length - 1];
    const table = last?.standings && last.standings.drivers.length > 0 ? last.standings : s.season.finalStandings;
    out.push({
      ...s,
      season: { ...s.season, races, championId: null, finalStandings: table, pointsStandings: table },
      offseason: undefined,
      complete: false,
    });
  }
  return out;
}

export function peopleContext(input: RivalryInput & { leagueKey?: string }): PeopleContext {
  const all = rivalrySeasons(input);
  const seasons = cutSeasons(all, input.throughRace);
  const book = rivalryState({ ...input, archive: seasons, live: null, throughRace: input.throughRace ?? null });
  const registry = buildRegistry(seasons);
  const streaks = driverStreaks(allRaces(seasons));
  return {
    leagueKey: input.leagueKey ?? leagueKeyOf(all),
    seasons,
    registry,
    streaks,
    book,
    asOf: book.asOf,
    cache: new Map(),
  };
}

/** A context with no history — an exhibition, a fresh league. */
export function emptyPeopleContext(leagueKey: string): PeopleContext {
  return peopleContext({ archive: [], leagueKey });
}

/** Who he is, as of the context's moment. */
export function personalityOf(driverId: string, ctx: PeopleContext): Personality {
  const hit = ctx.cache.get(driverId);
  if (hit) return hit;
  const entry = ctx.registry.byId[driverId] ?? null;
  let age: number | null = entry?.age ?? null;
  if (age === null) {
    for (let i = ctx.seasons.length - 1; i >= 0 && age === null; i--) {
      const card = ctx.seasons[i].roster.find((c) => c.driverId === driverId);
      if (card) age = ageOf(card);
    }
  }
  const p = derivePersonality(driverId, ctx.leagueKey, { entry, streak: ctx.streaks[driverId] ?? null, book: ctx.book, age }, entry?.teamName ?? null);
  ctx.cache.set(driverId, p);
  return p;
}
